import React, { useState, useEffect } from 'react';
import { useQueue } from '../contexts/QueueContext';

interface SyncStatusProps {
  // Abstand zur Bildschirmunterkante in px, damit die Leiste nicht hinter einer fixed
  // positionierten Bottom-Navigation verschwindet. Default 64 = Höhe der globalen
  // `BottomNavigation` (h-16). PhaseScreen nutzt stattdessen die höhere `PhaseFooterNav`
  // (siehe #1231-Review-Finding A).
  bottomOffsetPx?: number;
}

const SyncStatus: React.FC<SyncStatusProps> = ({ bottomOffsetPx = 64 }) => {
  const { syncStatus, isOnline, syncQueue, isProcessing } = useQueue();
  const { pending, lastSync, error, state, lastResult } = syncStatus;
  const [expanded, setExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Visibility basierend auf Status. `isProcessing` bewusst NICHT als Trigger (siehe #1231-
  // Analyse: die beiden Hotfixes 90d9e24/d751979 hatten die Bar erst wegen "ist im Weg" bei
  // jedem kurzen Sync eingeblendet und dann versehentlich komplett stumm geschaltet) - die Bar
  // erscheint nur, wenn tatsächlich etwas aussteht/fehlgeschlagen/offline ist, nicht bei jedem
  // schnellen Hintergrund-Sync.
  useEffect(() => {
    if (pending > 0 || error || !isOnline) {
      setIsVisible(true);
    } else {
      // Nach erfolgreicher Synchronisierung für kurze Zeit anzeigen, dann ausblenden
      if (lastSync) {
        setIsVisible(true);
        const timer = setTimeout(() => {
          setIsVisible(false);
          setExpanded(false);
        }, 5000);
        return () => clearTimeout(timer);
      } else {
        setIsVisible(false);
      }
    }
  }, [pending, error, isOnline, lastSync]);

  // Wenn nicht sichtbar, nichts anzeigen
  if (!isVisible) return null;

  // Formatierung des letzten Sync-Zeitpunkts
  const formatLastSync = (): string => {
    if (!lastSync) return 'Noch nie synchronisiert';
    
    const now = new Date();
    const syncDate = new Date(lastSync);
    
    // Zeitdifferenz in Minuten
    const diffMinutes = Math.round((now.getTime() - syncDate.getTime()) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Gerade eben';
    if (diffMinutes < 60) return `Vor ${diffMinutes} Minuten`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `Vor ${diffHours} Stunden`;
    
    // Datum formatieren für älteren Zeitpunkt
    return syncDate.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status-Klassen bestimmen (mit State Machine)
  const getStatusClasses = () => {
    if (error || state === 'failed') return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
    if (!isOnline) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
    if (state === 'syncing' || isProcessing) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 animate-pulse';
    if (state === 'completed') return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  };

  return (
    <div
      className="sync-status fixed inset-x-0 z-40 flex justify-center pb-[env(safe-area-inset-bottom)]"
      style={{ bottom: `${bottomOffsetPx}px` }}
      data-testid="sync-status"
      data-sync-state={state}
    >
      <div 
        className={`mx-4 mb-4 rounded-lg shadow-lg overflow-hidden transition-all duration-300 ${
          expanded ? 'max-w-md w-full' : 'w-auto max-w-xs'
        } ${getStatusClasses()}`}
      >
        {/* Header */}
        <div 
          className="p-3 flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center space-x-2">
            {/* Icon je nach Status */}
            {error ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : !isOnline ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
              </svg>
            ) : isProcessing ? (
              <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            )}

            {/* Status-Text mit State Machine */}
            <div>
              {error || state === 'failed' ? (
                <span>{error || 'Synchronisierung fehlgeschlagen'}</span>
              ) : !isOnline ? (
                <span>
                  <span className="offline-indicator" data-testid="offline-indicator"></span>
                  <span className="pending-count" data-testid="pending-count">{pending} {pending === 1 ? 'Datei' : 'Dateien'} warten (offline)</span>
                </span>
              ) : state === 'syncing' || isProcessing ? (
                <span data-testid="sync-status-syncing">Synchronisiere...</span>
              ) : state === 'completed' ? (
                <span data-testid="sync-status-synced">✅ Alle Dateien synchronisiert</span>
              ) : pending > 0 ? (
                <span className="pending-count" data-testid="pending-count">{pending} {pending === 1 ? 'Datei' : 'Dateien'} in Warteschlange</span>
              ) : (
                <span>Alle Dateien synchronisiert</span>
              )}
            </div>
          </div>
          
          <svg 
            className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
        
        {/* Expanded Content */}
        {expanded && (
          <div className="px-3 pb-3 border-t border-blue-200 dark:border-blue-800 pt-2">
            {lastSync && (
              <div className="mb-2 text-sm">
                <span className="font-medium">Letzter Sync:</span> {formatLastSync()}
              </div>
            )}
            
            {/* Result-Feedback wenn vorhanden */}
            {lastResult && (state === 'completed' || state === 'failed') && (
              <div className="mb-2 text-sm">
                <span className="font-medium">Ergebnis:</span> {lastResult.successful} erfolgreich
                {lastResult.failed > 0 && <span className="text-red-600 ml-1">({lastResult.failed} fehlgeschlagen)</span>}
              </div>
            )}
            
            {pending > 0 && isOnline && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  syncQueue();
                }}
                disabled={isProcessing}
                className={`manual-sync w-full py-2 px-3 text-sm font-medium rounded-md ${
                  isProcessing 
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                data-testid="manual-sync"
              >
                {isProcessing ? 'Synchronisiere...' : 'Jetzt synchronisieren'}
              </button>
            )}
            
            {error && (
              <div className="text-sm mt-1">
                <p>Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SyncStatus;
